/**
 * MatrixCodegenModal.tsx — the graph → matrix → NumPy generator.
 *
 * Opened from the StatusBar's code button (graph editor views). The user
 * picks a matrix kind (incidence / adjacency / Laplacian), an orientation,
 * where cell values come from (an edge param or 1) and what the input
 * vector x is (a node param, an edge param, or custom values). Everything
 * else is live: shapes, validation (can the matrix be generated at all?),
 * the matrix preview with the multiplied result column, and the copyable
 * standalone NumPy snippet.
 *
 * The math is the pure client-side core in `src/engine/matrix.ts` (instant,
 * works with no backend) — mirrored by the chat/MCP `graph_matrix` tool in
 * `backend/matrix.py`. Nothing here touches the wire contract; the config is
 * ephemeral modal state.
 */
import { useMemo, useState } from 'react';

import { Modal } from './Modal';
import {
  buildInput,
  buildMatrix,
  collectParamOptions,
  multiply,
  type InputSource,
  type MatrixIssue,
  type MatrixKind,
  type MatrixOrientation,
} from '../engine/matrix';
import { formatNumber, generateNumpy } from '../engine/matrixCodegen';
import type { Graph } from '../engine/types';

export interface MatrixCodegenModalProps {
  isOpen: boolean;
  graph: Graph | undefined;
  graphId?: string;
  graphLabel?: string;
  onClose: () => void;
}

/** Rows/cols rendered in the preview grid — beyond this the shape line +
 * generated code carry the information; a 300×300 DOM table helps nobody. */
const PREVIEW_CAP = 48;

/** The param key with the best element coverage — the friendliest default
 * after an axis flip ('weight' always covers every edge; node params vary). */
function bestParam(options: { key: string; count: number }[]): string {
  const best = [...options].sort((a, b) => b.count - a.count)[0];
  return best && best.count > 0 ? best.key : '';
}

interface Derived {
  build: ReturnType<typeof buildMatrix> | null;
  inputValues: number[] | null;
  result: number[] | null;
  issues: MatrixIssue[];
  code: string | null;
}

export function MatrixCodegenModal({ isOpen, graph, graphId, graphLabel, onClose }: MatrixCodegenModalProps) {
  const [kind, setKind] = useState<MatrixKind>('incidence');
  const [orientation, setOrientation] = useState<MatrixOrientation>('normal');
  const [cellParam, setCellParam] = useState<string>('weight');
  const [signed, setSigned] = useState(true);
  const [missingAsZero, setMissingAsZero] = useState(false);
  const [inputSource, setInputSource] = useState<InputSource>('edge');
  const [inputParam, setInputParam] = useState<string>('weight');
  const [customText, setCustomText] = useState('');
  const [tab, setTab] = useState<'matrix' | 'code'>('matrix');
  const [copied, setCopied] = useState(false);

  const opts = useMemo(() => (graph ? collectParamOptions(graph) : { nodes: [], edges: [] }), [graph]);

  /** Custom values: whitespace/comma separated; empty input = all zeros (the
   * friendliest default — the shape is what matters first). */
  const customValues = useMemo((): number[] | null => {
    if (inputSource !== 'custom' || !graph) return null;
    const tokens = customText.split(/[\s,]+/).filter(Boolean);
    if (tokens.length === 0) return null; // → zeros(cols) once cols are known
    const values = tokens.map(Number);
    return values.every((v) => Number.isFinite(v)) ? values : null; // parse error → shown below
  }, [inputSource, customText, graph]);

  const customParseError = inputSource === 'custom' && customText.trim() !== '' && customValues === null;

  const derived: Derived = useMemo(() => {
    if (!graph) {
      return { build: null, inputValues: null, result: null, issues: [], code: null };
    }
    const build = buildMatrix(graph, { kind, orientation, cellParam: cellParam || null, signed, missingAsZero });
    const inputConfig = inputSource === 'custom'
      ? { source: inputSource, param: null, values: customValues ?? new Array(build.cols.length).fill(0), missingAsZero }
      : { source: inputSource, param: inputParam || null, values: [], missingAsZero };
    const input = buildInput(graph, build, inputConfig);
    const issues = [...build.issues, ...input.issues];
    const inputValues = input.ok ? input.values : null;
    const result = build.ok && inputValues !== null ? multiply(build.cells, inputValues) : null;
    const inputNote = inputSource === 'custom'
      ? 'custom values'
      : `${inputSource} param '${inputParam}'${missingAsZero ? ', missing → 0' : ''}`;
    const code = build.ok
      ? generateNumpy(build, inputValues, {
          graphId: graphId ?? 'graph',
          graphLabel,
          inputNote,
          cellsNote: cellParam ? `edge param '${cellParam}'` : 'constant 1',
        })
      : null;
    return { build, inputValues, result, issues, code };
  }, [graph, kind, orientation, cellParam, signed, missingAsZero, inputSource, inputParam, customValues, graphId, graphLabel]);

  // Switching kind/orientation can flip the column axis between nodes and
  // edges — flip the input source with it (better than stranding the user on
  // the axis-mismatch error).
  const onKindChange = (next: MatrixKind): void => {
    setKind(next);
    autoFlipInput(next, orientation);
  };
  const onOrientationChange = (next: MatrixOrientation): void => {
    setOrientation(next);
    autoFlipInput(kind, next);
  };
  const autoFlipInput = (k: MatrixKind, o: MatrixOrientation): void => {
    const colAxisIsEdges = (k === 'incidence') !== (o === 'transpose');
    if (inputSource === 'node' && colAxisIsEdges) {
      setInputSource('edge');
      setInputParam('weight');
    } else if (inputSource === 'edge' && !colAxisIsEdges) {
      setInputSource('node');
      setInputParam(bestParam(opts.nodes));
    }
  };

  const copyCode = async (): Promise<void> => {
    if (!derived.code) return;
    try {
      await navigator.clipboard.writeText(derived.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard denied — the code stays selectable in the pane */ }
  };

  const build = derived.build;
  const errors = derived.issues.filter((i) => i.severity === 'error');
  const warnings = derived.issues.filter((i) => i.severity === 'warning');
  const inputParamOptions = inputSource === 'node' ? opts.nodes : opts.edges;
  const colCount = build?.cols.length ?? 0;
  const truncated = build !== null && (build.rows.length > PREVIEW_CAP || build.cols.length > PREVIEW_CAP);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xwide"
      headerLeft={
        <div className="agent-tabs" role="tablist">
          <button type="button" role="tab" aria-selected={tab === 'matrix'} className={`agent-tab${tab === 'matrix' ? ' on' : ''}`} onClick={() => setTab('matrix')}>Matrix</button>
          <button type="button" role="tab" aria-selected={tab === 'code'} className={`agent-tab${tab === 'code' ? ' on' : ''}`} onClick={() => setTab('code')}>Code</button>
        </div>
      }
      footer={
        <>
          <span className="wf-preview-note">
            {build
              ? `Matrix ${build.rows.length}×${build.cols.length} · input ${colCount} → result ${build.rows.length}`
              : 'No graph loaded.'}
          </span>
          <button
            type="button"
            className="wf-btn wf-btn-primary"
            onClick={copyCode}
            disabled={!derived.code}
            title={derived.code ? 'Copy the NumPy snippet' : 'Fix the errors first'}
          >
            {copied ? 'Copied ✓' : 'Copy code'}
          </button>
        </>
      }
    >
      {!graph ? (
        <div className="matrix-empty">No graph loaded — open a project first.</div>
      ) : (
        <div className="matrix-codegen">
          <div className="matrix-form">
            <label className="modal-field">
              <span className="field-label">Matrix</span>
              <select className="seg-select" value={kind} onChange={(e) => onKindChange(e.target.value as MatrixKind)}>
                <option value="incidence">Incidence · nodes × edges</option>
                <option value="adjacency">Adjacency · nodes × nodes</option>
                <option value="laplacian">Laplacian · nodes × nodes</option>
              </select>
            </label>
            <label className="modal-field">
              <span className="field-label">Orientation</span>
              <select className="seg-select" value={orientation} onChange={(e) => onOrientationChange(e.target.value as MatrixOrientation)}>
                <option value="normal">Normal</option>
                <option value="transpose">Transposed</option>
              </select>
            </label>
            <label className="modal-field">
              <span className="field-label">Cell values</span>
              <select className="seg-select" value={cellParam} onChange={(e) => setCellParam(e.target.value)}>
                <option value="">constant 1</option>
                {opts.edges.map((o) => (
                  <option key={o.key} value={o.key}>{o.key} · {o.count}/{graph.edges.length} edges</option>
                ))}
              </select>
            </label>
            {kind === 'incidence' && (
              <label className="matrix-check" title="−w at the source row, +w at the target row (flow conservation). Unchecked: counts, both +w.">
                <input type="checkbox" checked={signed} onChange={(e) => setSigned(e.target.checked)} />
                signed (±)
              </label>
            )}
            <label className="matrix-check" title="Elements missing the selected param become 0 (warning) instead of blocking the build.">
              <input type="checkbox" checked={missingAsZero} onChange={(e) => setMissingAsZero(e.target.checked)} />
              missing → 0
            </label>
            <label className="modal-field">
              <span className="field-label">Input x · per column ({colCount} {build?.colAxis ?? '—'})</span>
              <select
                className="seg-select"
                value={inputSource}
                onChange={(e) => {
                  const next = e.target.value as InputSource;
                  setInputSource(next);
                  if (next === 'edge') setInputParam('weight');
                  else if (next === 'node') setInputParam(bestParam(opts.nodes));
                }}
              >
                <option value="edge">edge param</option>
                <option value="node">node param</option>
                <option value="custom">custom values</option>
              </select>
            </label>
            {inputSource === 'custom' ? (
              <label className="modal-field">
                <span className="field-label">Values (whitespace / comma separated)</span>
                <input
                  className="seg-select matrix-custom-input"
                  type="text"
                  value={customText}
                  placeholder={`empty = ${colCount} zeros`}
                  onChange={(e) => setCustomText(e.target.value)}
                />
              </label>
            ) : (
              <label className="modal-field">
                <span className="field-label">Param</span>
                <select className="seg-select" value={inputParam} onChange={(e) => setInputParam(e.target.value)}>
                  <option value="">— pick param —</option>
                  {inputParamOptions.map((o) => (
                    <option key={o.key} value={o.key}>{o.key} · {o.count}/{inputSource === 'node' ? graph.nodes.length : graph.edges.length} {inputSource}s</option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {(errors.length > 0 || warnings.length > 0 || customParseError) && (
            <div className="matrix-issues">
              {customParseError && <div className="wf-preview-error">Custom values must be numbers.</div>}
              {errors.map((e, i) => <div key={i} className="wf-preview-error">{e.message}</div>)}
              {errors.length === 0 && warnings.map((w, i) => <div key={i} className="wf-preview-warn">{w.message}</div>)}
            </div>
          )}

          {tab === 'matrix' && build && (
            <div className="matrix-preview">
              {truncated && (
                <div className="wf-preview-warn matrix-truncated">
                  Showing the first {PREVIEW_CAP} rows/columns — the generated code is complete.
                </div>
              )}
              <div className="matrix-scroll">
                <table className="matrix-grid">
                  <thead>
                    <tr>
                      <th className="matrix-corner" title={`rows: ${build.rows.length} ${build.rowAxis}`} />
                      {build.cols.slice(0, PREVIEW_CAP).map((c) => (
                        <th key={c.id} title={c.label}>{c.id}</th>
                      ))}
                      {derived.result && <th className="matrix-result-head" title="b = M · x">b</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {build.rows.slice(0, PREVIEW_CAP).map((r, i) => (
                      <tr key={r.id}>
                        <th title={r.label}>{r.id}</th>
                        {build.cells[i].slice(0, PREVIEW_CAP).map((v, j) => (
                          <td key={j} className={v !== 0 ? 'nz' : undefined}>{v === 0 ? '·' : formatNumber(v)}</td>
                        ))}
                        {derived.result && <td className="matrix-result">{formatNumber(derived.result[i])}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'code' && (
            <pre className="wf-preview-src matrix-code">
              {derived.code ?? '// Fix the errors above — the code generates once the matrix is valid.'}
            </pre>
          )}
        </div>
      )}
    </Modal>
  );
}
